import React, { useCallback, useEffect, useState } from "react";
import {
  AssetStore,
  IAssetInAssetModel,
  IAssetModel,
  RecordStatus,
} from "@xala/common-services";
import {
  Button,
  Heading,
  Icon,
  ITableColumnProps,
  ITablePaginationConfig,
  Modal,
  NotificationService,
  Popconfirm,
  SectionGrid,
  SectionGridItem,
  TableWithDraggableSorter,
  Tag,
} from "@xala/common-ui";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AddAssetToAssetCollection } from "../AddAssetToAssetCollection";
import { ReloadOutlined } from "@ant-design/icons";

const notificationService = NotificationService.getInstance();

export interface IAssetInAssetListProps {
  title: string;
  asset?: IAssetModel;
}

export const AssetInAssetList: React.FC<IAssetInAssetListProps> = (props) => {
  const { title, asset } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { assetsInAssets, assetInAssets, action, isProcessing } = useSelector(
    AssetStore.Selectors.assetSelector
  );
  const [showAssetTableModal, setShowAssetTableModal] = useState<boolean>(
    false
  );
  const [statePagination, setStatePagination] = useState<
    ITablePaginationConfig
  >({
    pageSize: 10,
    total: 0,
    current: 1,
    showTotal: (total, range) =>
      t("ITEMS_RANGE_LABEL", {
        rangeFrom: range[0],
        rangeTo: range[1],
        total: total,
      }),
    showSizeChanger: true,
    defaultPageSize: 10,
    pageSizeOptions: ["10", "30", "50", "100"],
  });

  statePagination.total = assetsInAssets.TotalCount;

  const searchAssets = useCallback(() => {
    if (asset?.Id) {
      dispatch(AssetStore.Actions.selectAssetsInAsset(asset?.Id));
    }
  }, [dispatch]);
  const updateAssetInAsset = useCallback(
    (data: IAssetInAssetModel) =>
      dispatch(AssetStore.Actions.updateAssetInAsset(data)),
    [dispatch]
  );

  useEffect(() => {
    searchAssets();
  }, []);

  useEffect(() => {
    switch (action?.type) {
      case AssetStore.Consts.INSERT_ASSET_IN_ASSET_SUCCESS:
        searchAssets();
        notificationService.success({
          message: t(
            "INSERT_ASSET_IN_ASSET_SUCCESS",
            "Asset successfully added."
          ),
        });
        break;

      case AssetStore.Consts.UPDATE_ASSET_IN_ASSET_SUCCESS:
        searchAssets();
        break;
      case AssetStore.Consts.INSERT_ASSET_IN_ASSET_FAILURE:
        notificationService.error({
          message: t(
            "INSERT_ASSET_IN_ASSET_FAILURE",
            "There was an error while adding asset."
          ),
          description: action?.error?.Message,
        });
        break;
      case AssetStore.Consts.UPDATE_ASSET_IN_ASSET_FAILURE:
        notificationService.error({
          message: t(
            "UPDATE_ASSET_IN_ASSET_FAILURE",
            "There was an error while updating asset."
          ),
          description: action?.error?.Message,
        });
        break;
      case AssetStore.Consts.DELETE_ASSET_IN_ASSET_SUCCESS:
        notificationService.success({
          message: t(
            "DELETE_ASSET_IN_ASSET_SUCCESS",
            "Asset successfully deleted."
          ),
        });
        searchAssets();
        break;
      case AssetStore.Consts.DELETE_ASSET_IN_ASSET_FAILURE:
        notificationService.error({
          message: t(
            "DELETE_ASSET_IN_ASSET_FAILURE",
            "There was an error while deleting asset."
          ),
          description: action?.error?.Message,
        });
        break;
    }
  }, [action]);

  const getColumnsProps = (): Array<ITableColumnProps<IAssetInAssetModel>> => {
    return [
      {
        key: "Sequence",
        dataIndex: "Sequence",
        title: t("Order", "Order"),
        width: "100px",
      },
      {
        key: "AssetTitle",
        dataIndex: "AssetTitle",
        title: t("AssetTitle", "Title"),
        className: "drag-visible",
      },
      {
        key: "AssetTypeCode",
        dataIndex: "AssetTypeCode",
        title: t("AssetTypeCode", "Asset type code"),
        width: "200px",
        render: (text: any, row: IAssetInAssetModel) => {
          return (
            <Tag color={row.AssetTypeCode ? "#f50" : "#1890ff"}>
              {row.AssetTypeCode}
            </Tag>
          );
        },
      },
      {
        key: "RemoveAssetFromAssetAction",
        dataIndex: "RemoveAssetFromAssetAction",
        title: t("Action", "Action"),
        width: "150px",
        align: "center",
        render: (text: any, row: IAssetInAssetModel) => (
          <Popconfirm
            title={t("Are you sure you want to delete asset from asset?")}
            onConfirm={() => {
              dispatch(AssetStore.Actions.deleteAssetInAsset(row));
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<Icon type="delete" />}
              title={t("DeleteAssetFromAsset", "Delete asset from asset")}
              danger
            ></Button>
          </Popconfirm>
        ),
      },
    ];
  };

  const addAssetToCollection = (insertedAsset: IAssetModel) => {
    if (!insertedAsset || !insertedAsset.Id || !asset || !asset) {
      return;
    }

    const assetInAssetObject: IAssetInAssetModel = {
      AssetParentId: asset.Id,
      AssetId: insertedAsset.Id,
      AssetTitle: insertedAsset.Title ?? undefined,
      AssetTypeCode: insertedAsset.AssetTypeCode ?? undefined,
      RecordStatus: RecordStatus.Inserted,
    };

    dispatch(AssetStore.Actions.insertAssetInAsset(assetInAssetObject));
  };

  const onRefreshClick = () => searchAssets();

  const onTableChange = (pagination: ITablePaginationConfig) => {
    setStatePagination(pagination);
  };

  const onMoveRow = (dragIndex: number, hoverIndex: number) => {
    const draggedAsset: IAssetInAssetModel = assetsInAssets.Entities[dragIndex];
    const hoveredAsset: IAssetInAssetModel =
      assetsInAssets.Entities[hoverIndex];

    if (
      draggedAsset.Sequence === undefined ||
      hoveredAsset.Sequence === undefined
    ) {
      return;
    }

    const assetToUpdate: IAssetInAssetModel = {
      ...draggedAsset,
      Sequence: hoveredAsset.Sequence ?? 1,
    };

    const sequenceChanged = draggedAsset.Sequence !== assetToUpdate.Sequence;
    const draggedToNewPosition = draggedAsset.AssetId !== hoveredAsset.AssetId;

    if (draggedToNewPosition && sequenceChanged) {
      updateAssetInAsset(assetToUpdate);
    }
  };

  const renderAssetTableModal = () => {
    const assetsIds = assetsInAssets.Entities.map((asset) => asset.AssetId);

    return (
      <Modal
        width="90%"
        title={t("AddAssetToCollection", "Add asset to collection")}
        onCancel={() => {
          setShowAssetTableModal(false);
          searchAssets();
        }}
        visible={showAssetTableModal}
        footer={null}
      >
        <AddAssetToAssetCollection
          addAssetToCollection={addAssetToCollection}
          assetsIds={assetsIds}
        />
      </Modal>
    );
  };

  return (
    <>
      <SectionGrid>
        <SectionGridItem>
          <Heading
            title={title}
            actions={
              <>
                <Button
                  key="add"
                  shape="circle"
                  type="primary"
                  icon={<Icon type="plus" />}
                  onClick={() => setShowAssetTableModal(true)}
                  title={t("ADD_ASSET", "Add asset")}
                />
                <Button
                  key="reload"
                  shape="circle"
                  icon={<ReloadOutlined />}
                  onClick={onRefreshClick}
                  title="Refresh data"
                />
              </>
            }
          />
          <TableWithDraggableSorter<IAssetInAssetModel>
            dragType="handler"
            columns={getColumnsProps()}
            dataSource={assetsInAssets.Entities}
            rowKey="AssetId"
            loading={
              assetsInAssets.IsLoading ||
              assetInAssets.IsProcessing ||
              isProcessing
            }
            pagination={statePagination}
            onChange={onTableChange}
            onMoveRow={onMoveRow}
          />
        </SectionGridItem>
      </SectionGrid>
      {renderAssetTableModal()}
    </>
  );
};
